import { CSSObject, Theme, alpha, styled } from '@mui/material/styles';

const NARROW = '@container (max-width: 420px)';
const WIDE = '@container (min-width: 600px)';

export const accent = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? theme.palette.text.primary
    : theme.palette.primary.main;

export const mutedText = (theme: Theme): string =>
  alpha(theme.palette.text.primary, 0.72);

const hairline = (theme: Theme): string =>
  alpha(theme.palette.text.primary, 0.2);

const focusRing = (theme: Theme): CSSObject => ({
  outline: `2px solid ${accent(theme)}`,
  outlineOffset: 2,
});

const buttonBase = (theme: Theme): CSSObject => ({
  boxSizing: 'border-box',
  minHeight: 36,
  padding: theme.spacing(0.75, 1.75),
  borderRadius: 8,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.4,
  cursor: 'pointer',
  transition: 'background-color 140ms ease-out, border-color 140ms ease-out',
  '&:focus-visible': focusRing(theme),
  '&:disabled, &[aria-disabled="true"]': { cursor: 'default', opacity: 0.6 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

export const CardFrame = styled('div')({
  containerType: 'inline-size',
  width: '100%',
  maxWidth: 720,
  minWidth: 0,
});

export const CardRoot = styled('section')(({ theme }) => ({
  isolation: 'isolate',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${hairline(theme)}`,
  borderLeft: `3px solid ${accent(theme)}`,
  borderRadius: 10,
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  minWidth: 0,
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
    { borderColor: accent(theme) },
  '& .MuiInputLabel-root:not(.Mui-error)': { color: mutedText(theme) },
  '& .MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
    color: accent(theme),
  },
  '& .MuiInputAdornment-root, & .MuiInputAdornment-root .MuiTypography-root': {
    color: mutedText(theme),
  },
  '& .MuiFormHelperText-root:not(.Mui-error)': { color: mutedText(theme) },
  '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': focusRing(theme),
  [NARROW]: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
  },
}));

export const CardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const CardEyebrow = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  borderRadius: 4,
  '&:focus-visible': focusRing(theme),
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: mutedText(theme),
}));

export const CardTitle = styled('h3')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 700,
  lineHeight: 1.3,
  overflowWrap: 'anywhere',
  borderRadius: 4,
  '&:focus-visible': focusRing(theme),
}));

export const TitlePrefix = styled('span')(({ theme }) => ({
  fontWeight: 600,
  color: mutedText(theme),
}));

export const BlockedNote = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.5,
  color: mutedText(theme),
}));

export const SafetyNote = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.5,
  color: mutedText(theme),
}));

export const CardLead = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  maxWidth: '70ch',
  color: mutedText(theme),
}));

export const CardSentence = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  maxWidth: '70ch',
  overflowWrap: 'anywhere',
}));

export const CardBadge = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  boxSizing: 'border-box',
  padding: theme.spacing(0.25, 1),
  borderRadius: 999,
  border: `1px solid ${theme.palette.warning.main}`,
  background: alpha(theme.palette.warning.main, 0.12),
  color: theme.palette.text.primary,
  fontWeight: 700,
}));

export const StepList = styled('ol')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  margin: 0,
  padding: 0,
  listStyle: 'none',
  [NARROW]: { display: 'none' },
}));

export type StepState = 'done' | 'error' | 'todo';

const chipBorder = (
  theme: Theme,
  state: StepState,
  current: boolean,
): string => {
  if (state === 'error') {
    return theme.palette.error.main;
  }
  return current ? theme.palette.text.primary : hairline(theme);
};

export const StepChip = styled('button', {
  shouldForwardProp: prop => prop !== 'state' && prop !== 'current',
})<{ state: StepState; current: boolean }>(({ theme, state, current }) => ({
  ...buttonBase(theme),
  minHeight: 32,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 999,
  border: `1px solid ${chipBorder(theme, state, current)}`,
  background: current ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
  color: theme.palette.text.primary,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: current ? 700 : 600,
  '&:hover:not(:disabled)': {
    background: alpha(theme.palette.text.primary, 0.08),
  },
  '&:disabled': {
    cursor: 'default',
    opacity: 1,
    color: current ? theme.palette.text.primary : mutedText(theme),
    borderStyle: current ? 'solid' : 'dashed',
  },
}));

export const StepDot = styled('span')(({ theme }) => ({
  flexShrink: 0,
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: theme.palette.error.main,
}));

export const StepBody = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

export const StepHeading = styled('h4')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 700,
  lineHeight: 1.4,
  borderRadius: 4,
  '&:focus-visible': focusRing(theme),
}));

export const StepAttention = styled('p')(({ theme }) => ({
  ...theme.typography.caption,
  display: 'none',
  margin: 0,
  color: theme.palette.error.main,
  fontWeight: 700,
  [NARROW]: { display: 'block' },
}));

export const ChangedChip = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  display: 'inline-block',
  marginLeft: theme.spacing(1),
  padding: theme.spacing(0, 0.75),
  borderRadius: 999,
  border: `1px solid ${hairline(theme)}`,
  color: theme.palette.text.primary,
  fontWeight: 700,
  lineHeight: 1.6,
}));

export const AlertLine = styled('span')(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(0.5),
}));

export const CardRecipients = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  fontWeight: 700,
  '& > span': { fontWeight: 400, color: mutedText(theme) },
}));

export const FieldGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: theme.spacing(2),
  minWidth: 0,
  [WIDE]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

export const FieldCell = styled('div', {
  shouldForwardProp: prop => prop !== 'wide',
})<{ wide: boolean }>(({ wide }) => ({
  minWidth: 0,
  gridColumn: wide ? '1 / -1' : undefined,
}));

export const FieldHint = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  display: 'block',
  margin: theme.spacing(0.5, 1.75, 0),
  color: mutedText(theme),
  fontVariantNumeric: 'tabular-nums',
}));

export const FieldNote = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 1.5),
  borderRadius: 8,
  background: alpha(theme.palette.text.primary, 0.04),
  '& > p': { margin: 0, maxWidth: '70ch' },
}));

export const FieldSet = styled('fieldset')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: 0,
  borderRadius: 4,
  '&:focus-visible': focusRing(theme),
  '& > legend': {
    ...theme.typography.body2,
    padding: 0,
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}));

export const RowCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  minWidth: 0,
  padding: theme.spacing(1.5),
  border: `1px solid ${hairline(theme)}`,
  borderRadius: 8,
}));

export const RowHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  ...theme.typography.body2,
  fontWeight: 700,
}));

export const HoursRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  minWidth: 0,
  '& > .alpha-hours-day': { flex: '1 1 150px', minWidth: 0 },
  '& > .alpha-hours-time': { flex: '1 1 112px', minWidth: 0 },
}));

export const EmptyLine = styled('p')(({ theme }) => ({
  ...theme.typography.body2,
  margin: 0,
  color: mutedText(theme),
}));

export const LoadProblem = styled('div')(({ theme }) => ({
  ...theme.typography.caption,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  margin: theme.spacing(0.5, 1.75, 0),
  color: theme.palette.error.main,
}));

export const OptionLabel = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const OptionDetail = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: mutedText(theme),
}));

export const ReviewGroup = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${hairline(theme)}`,
  '&:first-of-type': { paddingTop: 0, borderTop: 0 },
}));

export const ReviewGroupHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

export const ReviewGroupTitle = styled('h5')(({ theme }) => ({
  margin: 0,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 700,
  lineHeight: 1.4,
}));

export const ReviewList = styled('dl')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(0.75),
  margin: 0,
  '& > dt': { color: mutedText(theme) },
  '& > dd': {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  [NARROW]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    rowGap: 0,
    '& > dd': { marginBottom: theme.spacing(1) },
  },
}));

export const ReadOnlyList = styled('dl')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  margin: 0,
  padding: theme.spacing(0.5, 0),
  '& > dt': {
    fontSize: theme.typography.caption.fontSize,
    color: mutedText(theme),
  },
  '& > dd': {
    margin: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
}));

export const NotSet = styled('span')(({ theme }) => ({
  color: mutedText(theme),
}));

export const CardFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const FooterSpacer = styled('span')({
  flex: '1 1 auto',
});

export const PrimaryButton = styled('button')(({ theme }) => ({
  ...buttonBase(theme),
  border: `1px solid ${accent(theme)}`,
  background: accent(theme),
  color: theme.palette.background.paper,
  fontWeight: 700,
  '&:hover:not(:disabled):not([aria-disabled="true"])': {
    background: alpha(accent(theme), 0.85),
  },
}));

export const SecondaryButton = styled('button')(({ theme }) => ({
  ...buttonBase(theme),
  border: `1px solid ${alpha(theme.palette.text.primary, 0.32)}`,
  background: 'transparent',
  color: theme.palette.text.primary,
  fontWeight: 600,
  '&:hover:not(:disabled)': {
    background: alpha(theme.palette.text.primary, 0.06),
  },
}));

export const LinkButton = styled('button')(({ theme }) => ({
  ...buttonBase(theme),
  minHeight: 0,
  padding: theme.spacing(0.25, 0.5),
  border: 0,
  borderRadius: 4,
  background: 'transparent',
  color: 'inherit',
  fontSize: 'inherit',
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  '&:hover:not(:disabled)': {
    background: alpha(theme.palette.text.primary, 0.06),
  },
}));

export const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
